import { Button, Input } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import * as React from 'react'
import { describeError } from '../../lib/api/client'
import type { UnroutedInboundOut } from '../../lib/api/generated'
import { inboundCaseSearchQuery } from '../notifications/api'
import { useFileSentMail } from './api'

/** Unrouted mail outside Desk. A person picks a case and files it on the internal pane. */
export function SentMailUnroutedCard({ message }: { message: UnroutedInboundOut }) {
  const initialCase = message.suggested_case_ids?.[0] ?? null
  const [caseId, setCaseId] = React.useState<string | null>(initialCase)
  const [caseLabel, setCaseLabel] = React.useState(initialCase ? 'Suggested case' : '')
  const [search, setSearch] = React.useState('')
  const searchQuery = useQuery(inboundCaseSearchQuery(search.trim()))
  const file = useFileSentMail(caseId ?? '')
  const suggestionId = message.sent_mail_id ?? ''

  return (
    <article
      id={`inbound-${message.id}`}
      className="space-y-3 rounded-lg border border-border bg-card p-4"
      data-testid="sent-mail-unrouted"
    >
      <div>
        <p className="font-medium">Unrouted mail outside Desk</p>
        <p className="text-sm">{message.subject || '(no subject)'}</p>
        <p className="text-sm text-muted-foreground">{message.from_name || message.from_addr || 'Unknown sender'}</p>
      </div>
      {message.excerpt ? <p className="line-clamp-4 whitespace-pre-wrap text-sm">{message.excerpt}</p> : null}
      <p className="text-sm text-muted-foreground">
        File this on the internal pane. Nothing is emailed, and it does not go on the customer pane.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <div className="relative min-w-0 flex-1">
          <Input
            aria-label="Find case"
            placeholder="Find case by C-number, customer, or subject"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {search.trim() && searchQuery.data?.length ? (
            <div className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-lg">
              {searchQuery.data.map((hit) => (
                <button
                  key={hit.id}
                  type="button"
                  className="block w-full cursor-pointer rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                  onClick={() => {
                    setCaseId(hit.id)
                    setCaseLabel(hit.label)
                    setSearch('')
                  }}
                >
                  {hit.label}
                </button>
              ))}
            </div>
          ) : null}
          {caseId ? <p className="mt-1 truncate text-xs text-muted-foreground">{caseLabel}</p> : null}
        </div>
        <Button
          disabled={!caseId || !suggestionId}
          pending={file.isPending}
          onClick={() => suggestionId && caseId && file.mutate(suggestionId)}
        >
          File on internal
        </Button>
      </div>
      {searchQuery.isError || file.isError ? (
        <p className="text-sm text-warning">{describeError(searchQuery.error || file.error)}</p>
      ) : null}
    </article>
  )
}
